// PhysicianPicker — the "Brightree moment": one search box that finds a
// physician in the directory by name, NPI, practice or specialty, and hands
// the whole record back so the form can fill itself.
//
//   <PhysicianPicker value={physician} onChange={p => ...} onAddNew={q => ...} />
//
// `value` is a physician_directory row (or null). onAddNew(query) fires when
// the user wants someone who isn't listed; the caller opens PhysicianForm.
// Pass `physicians` to reuse an already-loaded list; otherwise it loads one.
import { useEffect, useMemo, useRef, useState } from 'react';
import { listPhysicians, searchPhysicians, fmtPhone } from '../../lib/directory';
import { RefCode, Pill } from './ui';

export default function PhysicianPicker({ value, onChange, onAddNew, physicians: given, placeholder = 'Search by name, NPI, practice or specialty…', autoFocus = false, disabled = false, exclude = [], addLabel, noMatchLabel }) {
  const [own, setOwn] = useState(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const box = useRef(null);

  useEffect(() => {
    if (given) return;
    listPhysicians().then(({ data }) => setOwn((data || []).filter(p => p.status === 'active')));
  }, [given]);
  const results = useMemo(() => {
    const all = given || own || [];
    const ex = new Set(exclude);
    return searchPhysicians(all, q).filter(p => !ex.has(p.id)).slice(0, 8);
  }, [given, own, q, exclude]);

  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (p) => { onChange(p); setQ(''); setOpen(false); };

  if (value) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--success-bg)', border: '1px solid var(--success-border)', borderRadius: 8, padding: '8px 10px', fontSize: 13, flexWrap: 'wrap' }}>
        <RefCode code={value.ref_code} />
        <b>{value.display_name || `${value.last_name}, ${value.first_name}`}</b>
        {value.credentials && <span style={{ color: 'var(--text2)' }}>{value.credentials}</span>}
        {value.specialty && <span style={{ color: 'var(--text3)' }}>· {value.specialty}</span>}
        {value.facility_name && <span style={{ color: 'var(--text3)' }}>· {value.facility_name}</span>}
        {!disabled && (
          <button type="button" onClick={() => onChange(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit' }}>Change</button>
        )}
      </div>
    );
  }

  return (
    <div ref={box} style={{ position: 'relative' }}>
      <input className="form-input" value={q} autoFocus={autoFocus} disabled={disabled}
        placeholder={placeholder}
        onChange={e => { setQ(e.target.value); setOpen(true); setHi(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={e => {
          if (!open) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(h + 1, results.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter') { e.preventDefault(); if (results[hi]) pick(results[hi]); else if (q.trim() && onAddNew) { onAddNew(q.trim()); setQ(''); setOpen(false); } }
          else if (e.key === 'Escape') setOpen(false);
        }} />
      {open && (q.trim() || results.length > 0) && (
        <div style={{ position: 'absolute', zIndex: 50, left: 0, right: 0, top: '100%', marginTop: 4, background: 'var(--card-bg)', border: '1px solid var(--border2)', borderRadius: 10, boxShadow: '0 12px 40px rgba(10,22,40,0.18)', overflow: 'hidden' }}>
          {results.map((p, i) => (
            <div key={p.id} onMouseDown={() => pick(p)} onMouseEnter={() => setHi(i)}
              style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 10, alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 13, cursor: 'pointer', background: i === hi ? 'var(--accent-bg)' : 'transparent' }}>
              <RefCode code={p.ref_code} />
              <div style={{ minWidth: 0 }}>
                <div><b>{p.display_name}</b>{p.credentials ? ` ${p.credentials}` : ''}{p.specialty ? <span style={{ color: 'var(--text2)' }}> · {p.specialty}</span> : null}</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {[p.facility_name, p.city, 'npi' in p ? (p.npi ? `NPI ${p.npi}` : 'no NPI') : null, p.fax ? `fax ${fmtPhone(p.fax)}` : null].filter(Boolean).join(' · ')}
                </div>
              </div>
              <span style={{ fontSize: 11.5, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{p.client_count ? `${p.client_count} client${p.client_count === 1 ? '' : 's'}` : ''}</span>
            </div>
          ))}
          {results.length === 0 && <div style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--text3)' }}>{noMatchLabel ? noMatchLabel(q) : <>No one in the directory matches “{q}”.</>}</div>}
          {onAddNew && (
            <div onMouseDown={() => { onAddNew(q.trim()); setQ(''); setOpen(false); }} style={{ padding: '8px 12px', fontSize: 12.5, color: 'var(--accent)', background: 'var(--bg2)', cursor: 'pointer' }}>
              {addLabel ? addLabel(q.trim()) : <>Not in the list? <b>+ Add {q.trim() ? `“${q.trim()}”` : 'a physician'}</b> <Pill tone="neutral" style={{ marginLeft: 6 }}>looks up NPPES</Pill></>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
